import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import AddressAutocomplete from '../components/shared/AddressAutocomplete';
import { PROMO, isPromoActive } from '../config/promoConfig';
import './FormPage.css';

/* ════════════════════════════════════════════════════════════
   Book Service form
   Writes to the `leads` collection.
   Accepts prefill state from /services:
     - prefilledIssue (string)
     - flatRateServices (array of { id, name, price })
     - flatRateTotal (number)
   ════════════════════════════════════════════════════════════ */

export default function Book() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: null,
    issue: prefill.prefilledIssue || '',
    timing: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const flatRateServices = prefill.flatRateServices || null;
  const flatRateTotal = prefill.flatRateTotal || null;
  const prefilledRegion = prefill.region || null;
  const hasFlatRate = flatRateServices && flatRateServices.length > 0;

  // Back: go home + scroll to pricing
  const goToPricing = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('pricing');
      if (!el) return;
      const NAV_HEIGHT = window.innerWidth <= 768 ? 110 : 90;
      const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_HEIGHT;
      window.scrollTo({ top: y, behavior: 'instant' });
    }, 50);
  };

  // Logo: go to top of homepage
  const goToTop = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    if (!form.name.trim()) return setError('Please enter your name.');
    if (!form.phone.trim()) return setError('Please enter a phone number.');
    if (!form.email.trim()) return setError('Please enter an email.');
    if (!form.address || !form.address.formatted) return setError('Please select the service address from the suggestions.');
    if (!form.issue.trim()) return setError('Please describe what needs doing.');
    if (!form.timing.trim()) return setError("Please tell us when you'd like service.");

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'leads'), {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        address: form.address,
        issue: form.issue.trim(),
        timing: form.timing.trim(),
        source: hasFlatRate ? 'flat-rate-menu' : 'book-form',
        flatRateServices: flatRateServices || null,
        flatRateTotal: flatRateTotal || null,
        pricingRegion: prefilledRegion || null,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Submit failed', err);
      setError('Something went wrong. Please try again or call us at (516) 386-9923.');
    } finally {
      setSubmitting(false);
    }
  };

  // Shared topbar (matches /services and /estimate)
  const Topbar = () => (
    <header className="svc-topbar">
      <a href="/#pricing" onClick={goToPricing} className="svc-back">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back
      </a>
      <a href="/" onClick={goToTop} className="svc-brand">
        <img src={require('../assets/logo2.png')} alt="Reliable Handyman" className="svc-brand-img" />
      </a>
      <div className="svc-topbar-spacer" aria-hidden="true" />
    </header>
  );

  // Shared footer (matches /services and /estimate)
  const Footer = () => (
    <footer className="svc-marketing-footer">
      <div className="svc-container svc-marketing-footer-inner">
        <a href="/" onClick={goToTop} className="svc-marketing-footer-brand" style={{ textDecoration: 'none' }}>
          <img src={require('../assets/logo2.png')} alt="Reliable Handyman" style={{ height: 26 }} />
        </a>
        <div className="svc-marketing-footer-contact">
          <a href="tel:+15163869923">(516) 386-9923</a>
          <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>
        </div>
      </div>
    </footer>
  );

  if (submitted) {
    return (
      <div className="rh-formpage">
        <Topbar />
        <div className="rh-formpage__inner rh-formpage__success">
          <div className="rh-success-card">
            <div className="rh-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" fill="#EAF7F0" />
                <path d="M7 12.5l3.5 3.5L17 9" stroke="#1D6B45" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1>Got it, {form.name.split(' ')[0]}.</h1>
            <p>
              {hasFlatRate
                ? `Your request for ${flatRateServices.length} service${flatRateServices.length !== 1 ? 's' : ''} is in. We'll reach out shortly to confirm timing.`
                : "Your request is in. We'll reach out shortly to confirm timing and answer any questions."}
            </p>
            <p>Need us sooner? Call <a href="tel:+15163869923">(516) 386-9923</a>.</p>
            <div className="rh-success-actions">
              <button onClick={goToPricing} className="rh-form-btn">Back to pricing</button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="rh-formpage">
      <Topbar />

      <div className="rh-formpage__inner">
        <header className="rh-formpage__header">
          <div className="rh-formpage__eyebrow">Book Service</div>
          <h1>Tell us about the job.</h1>
          <p className="rh-formpage__lede">
            Tell us a bit about the job and we'll reach back to confirm a time. We respond same-day during business hours.
          </p>
        </header>

        {isPromoActive() && (
          <div className="rh-promo-notice">
            <div className="rh-promo-notice__tag">{PROMO.eyebrowLabel}</div>
            <div className="rh-promo-notice__body">
              All new customers receive {PROMO.percentOff}% off (up to ${PROMO.capDollars}) when payment is collected. Prices shown don't reflect this discount.
            </div>
          </div>
        )}

        {hasFlatRate && (
          <div className="rh-flatrate-summary">
            <div className="rh-flatrate-label">Your selected services</div>
            <ul className="rh-flatrate-list">
              {flatRateServices.map(s => (
                <li key={s.id}>
                  <span>{s.name}</span>
                  <span className="rh-flatrate-price">{s.price != null ? `$${s.price}` : '$XXX'}</span>
                </li>
              ))}
            </ul>
            {flatRateTotal != null && flatRateTotal > 0 && (
              <div className="rh-flatrate-total">
                <span>Total</span>
                <span>${flatRateTotal}</span>
              </div>
            )}
            <Link to="/services" className="rh-flatrate-edit">← Edit selection</Link>
          </div>
        )}

        <form className="rh-form" onSubmit={handleSubmit} noValidate>
          <div className="rh-form-grid">
            <div className="rh-field">
              <label htmlFor="name">Your name</label>
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Smith"
                autoComplete="name"
                required
              />
            </div>

            <div className="rh-field">
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="(516) 555-1234"
                autoComplete="tel"
                required
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="address">Service address</label>
              <AddressAutocomplete
                value={form.address}
                onChange={(addr) => setForm({ ...form, address: addr })}
                placeholder="123 Main St, Lindenhurst, NY 11757"
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="issue">
                {hasFlatRate ? 'Anything else we should know?' : 'What needs doing?'}
              </label>
              <textarea
                id="issue"
                value={form.issue}
                onChange={e => setForm({ ...form, issue: e.target.value })}
                placeholder={hasFlatRate
                  ? "Optional — any details about access, timing, parking, etc."
                  : "Leaky kitchen faucet, won't stop dripping. Already tried tightening the cap."}
                rows={4}
                required={!hasFlatRate}
              />
            </div>

            <div className="rh-field rh-field--full">
              <label htmlFor="timing">When would you like service?</label>
              <input
                id="timing"
                type="text"
                value={form.timing}
                onChange={e => setForm({ ...form, timing: e.target.value })}
                placeholder="ASAP, this week, next weekend, etc."
                required
              />
            </div>
          </div>

          {error && (
            <div className="rh-form-error" role="alert">{error}</div>
          )}

          <div className="rh-form-actions">
            <button
              type="submit"
              className="rh-form-btn"
              disabled={submitting}
            >
              {submitting ? 'Sending...' : 'Submit Request'}
            </button>
            <div className="rh-form-disclaimer">
              By submitting, you agree we can contact you about your service
              request by phone, email, or text. Message and data rates may
              apply; reply STOP to opt out of texts. See our{' '}
              <Link to="/privacy">Privacy Policy</Link> and{' '}
              <Link to="/terms">Terms</Link>.
            </div>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
}
